import React, {useEffect} from 'react';
import {useToast} from '@chakra-ui/react';

/*
 * Will automatically show an error popup for any api response if included on the page.
 * Can pass in a json with custom success or error messages if needed.  Listens for any
 * changes to the toast message, if it changes or is called we will run the code below
 *
 * TO USE:
 * 1) at the top of the page do const [toastResponse, setToastResponse] = useState(undefined)
 * 2) somewhere on the return of the page include the component <ErrorHelper toastResponse={toastResponse}/>
 * 3) when fetching data, after saying something like userResponse = post(data);
 *    can call this by updating the toastResponse in any combination of the following ways:
 *                                                 setToastResponse(userResponse);
 *                                                 setToastResponse({response:userResponse, successMessage:"Congrats!", errorMessage:"Shoot!",
 *                                                                   onError:myErrorFunction, onSuccess:mySuccessFunction});
 *                                                 setToastResponse({response:userResponse, successMessage:"Congrats!"});
 *                                                 setToastResponse({response:userResponse, onSuccess:()=>myFunFunction(fun), errorMessage:"Nice try!"});
 *
 *
 * @param {toastResponse}
 * toastResponse format:
 * {
 *  response: api response ,                                          --required
 *  successMessage: string to be displayed if response is successful  --optional - defaults to not showing a success message
 *  errorMessage: string to be displayed if response is an error      --optional - defaults to response error message
 *  onSuccess: function to run on success                             --optional - default does nothing
 *  onError: function to run on error                                 --optional - default just toasts the error
 * }
 * @returns burnt toast
 */
const ErrorHelper = ({toastResponse}) => {
  const toast = useToast();

  useEffect(() => {
    // This line is so that we can pass in just the response if we want rather than a json of {response:response}
    const response = toastResponse?.response ? toastResponse.response : toastResponse;
    if (response) {
      if (response?.error) {
        toast({
          title: 'Error',
          description: toastResponse?.errorMessage ? toastResponse.errorMessage : response.error,
          status: 'error',
          duration: 9000,
          isClosable: true,
        });
        if (toastResponse?.onError) {
          toastResponse.onError();
        }
      } else {
        if (toastResponse?.successMessage) {
          toast({
            title: 'Success',
            description: toastResponse.successMessage,
            status: 'success',
            duration: 9000,
            isClosable: true,
          });
        }

        if (toastResponse?.onSuccess) {
          toastResponse.onSuccess();
        }
      }
    }
  }, [toastResponse]);

  return <></>;
};

export default ErrorHelper;
